import { Node } from "#/decision_tree_viewer/models/node";

import { isNil, isUndefined } from "lodash";

export class ConditionNode extends Node {
  get defaults() {
    return {
      ...super.defaults,
      name: "ConditionNode",

      matchType: "all", // "all" or "any"
      rules: [],

      matchTargetNodeId: "continue",
      noMatchTargetNodeId: "continue",
    };
  }

  get possibleNextNodeIds() {
    return [this.matchTargetNodeId, this.noMatchTargetNodeId];
  }

  autoAdvance() {
    return true;
  }

  nextNodeId(context) {
    if (this.conditionMatches(context)) {
      return this.matchTargetNodeId;
    }

    return this.noMatchTargetNodeId;
  }

  conditionMatches(context) {
    if (this.matchType === "all") {
      return this.rules.every((rule) => this.ruleMatches(rule, context));
    }

    return this.rules.some((rule) => this.ruleMatches(rule, context));
  }

  ruleMatches(rule, context) {
    const match = this[`${rule.type}Match`](rule, context);

    return rule.negate ? !match : match;
  }

  ageGroupsMatch(rule, context) {
    if (isUndefined(context.minimumAge) && isUndefined(context.maximumAge)) {
      return true;
    }

    const contextInAgeRange = context.inAgeRange;
    const contextMinimumAge = context.minimumAge ?? 0;
    const contextMaximumAge = context.maximumAge ?? 200;

    return rule.value.some((ageGroup) => {
      const ruleMinimumAge = ageGroup.from ?? 0;
      const ruleMaximumAge = ageGroup.to ?? 200;

      // If the patient picked the valid choice from the age validation node, check if it covers the rule's age range
      if (contextInAgeRange) {
        return ruleMinimumAge >= contextMinimumAge && ruleMaximumAge <= contextMaximumAge;
      }

      // If the patient picked the invalid choice from the age validation node,
      // check if the rule's age range is outside of the context's age range
      return (
        ruleMinimumAge > contextMaximumAge ||
        ruleMaximumAge < contextMinimumAge ||
        (ruleMinimumAge > contextMinimumAge && ruleMaximumAge < contextMaximumAge)
      );
    });
  }

  specialtiesMatch(rule, context) {
    if (isUndefined(context.providerSpecialtyIds)) {
      return true;
    }

    return rule.value.some((specialtyId) => context.providerSpecialtyIds.includes(specialtyId));
  }

  locationsMatch(rule, context) {
    if (isUndefined(context.providerLocationId)) {
      return true;
    }

    return rule.value.includes(context.providerLocationId);
  }

  providerNetworksMatch(rule, context) {
    if (isUndefined(context.providerNetworkIds)) {
      return true;
    }

    return rule.value.some((networkId) => context.providerNetworkIds.includes(networkId));
  }

  providerTypesMatch(rule, context) {
    if (isUndefined(context.providerTypeIds)) {
      return true;
    }

    return rule.value.some((providerTypeId) => context.providerTypeIds.includes(providerTypeId));
  }

  providerGroupsMatch(rule, context) {
    if (isUndefined(context.providerGroupIds)) {
      return true;
    }

    return rule.value.some((providerGroupId) => context.providerGroupIds.includes(providerGroupId));
  }

  // `isNil`, not `isUndefined`: an unresolved department can reach the context
  // as an explicit null, and it must take the same "unknown, pass" path as an
  // absent one rather than the no-match branch.
  departmentsMatch(rule, context) {
    if (isNil(context.providerDepartmentId)) {
      return true;
    }

    return rule.value.includes(context.providerDepartmentId);
  }

  patientTypesMatch(rule, context) {
    if (isUndefined(context.patientType)) {
      return true;
    }

    return rule.value === context.patientType;
  }

  visitTypesMatch(rule, context) {
    if (isUndefined(context.visitTypeId)) {
      return true;
    }

    return rule.value.includes(context.visitTypeId);
  }

  visitTypeCategoriesMatch(rule, context) {
    if (isUndefined(context.visitType)) {
      return true;
    }

    return rule.value.includes(context.visitType.category);
  }
}
